import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  MapPin,
  Clock,
  DollarSign,
  Users,
  Zap,
  Shield,
  Code,
  Database,
  Cloud,
  Brain,
  Truck,
  Briefcase,
  Target,
  ShoppingCart,
  Megaphone,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAuth0Context } from "@/contexts/Auth0Context";
import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";

export const Careers = () => {
  const { isAuthenticated: legacyAuth, logout: legacyLogout } = useAuth();
  const {
    isAuthenticated: auth0Authenticated,
    loginWithRedirect,
    isLoading,
    user: auth0User,
    logout: auth0Logout,
  } = useAuth0Context();

  const handleSignIn = async () => {
    try {
      await loginWithRedirect();
    } catch (error) {
      console.error("Sign in error:", error);
    }
  };

  const handleSignUp = async () => {
    try {
      // For Auth0, we can pass screen_hint=signup to show signup form
      await loginWithRedirect();
    } catch (error) {
      console.error("Sign up error:", error);
    }
  };

  // Show loading state while Auth0 is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  const jobPositions = [
    {
      id: "ai-engineer",
      title: "AI Engineer",
      department: "Engineering",
      location: "Remote / Hybrid",
      icon: Brain,
      description:
        "Join our AI team to build and optimize machine learning models for document processing and data extraction.",
      responsibilities: [
        "Design and implement AI/ML models for document intelligence and OCR",
        "Optimize model performance for accuracy and speed",
        "Develop and maintain AI pipelines for production environments",
        "Collaborate with data scientists and software engineers",
        "Research and implement cutting-edge AI technologies",
        "Ensure AI models meet accuracy and compliance requirements",
      ],
      requirements: [
        "Strong background in machine learning, deep learning, and computer vision",
        "Experience with Python, TensorFlow, PyTorch, or similar frameworks",
        "Knowledge of OCR, NLP, and document processing techniques",
        "Experience with cloud platforms (Azure, AWS, GCP)",
        "Understanding of MLOps and model deployment",
        "Excellent problem-solving and analytical skills",
      ],
    },
    {
      id: "ai-software-developer",
      title: "AI Software Developer",
      department: "Engineering",
      location: "Remote / Hybrid",
      icon: Code,
      description:
        "Develop software solutions that integrate AI capabilities into our logistics platform.",
      responsibilities: [
        "Build and maintain AI-powered software applications",
        "Integrate machine learning models into production systems",
        "Develop APIs and microservices for AI functionality",
        "Implement automated testing for AI components",
        "Optimize application performance and scalability",
        "Collaborate with cross-functional teams",
      ],
      requirements: [
        "Proficiency in Python, C#, JavaScript, or similar languages",
        "Experience with AI/ML libraries and frameworks",
        "Knowledge of software development best practices",
        "Experience with cloud services and APIs",
        "Understanding of DevOps and CI/CD pipelines",
        "Strong debugging and problem-solving skills",
      ],
    },
    {
      id: "ux-ui-developer",
      title: "UX/UI Software Developer",
      department: "Design & Engineering",
      location: "Remote / Hybrid",
      icon: Users,
      description:
        "Create intuitive and beautiful user experiences for our logistics platform.",
      responsibilities: [
        "Design and implement user interfaces for web applications",
        "Create responsive and accessible UI components",
        "Conduct user research and usability testing",
        "Collaborate with product managers and developers",
        "Maintain design systems and component libraries",
        "Optimize user experience and interface performance",
      ],
      requirements: [
        "Strong proficiency in React, TypeScript, and modern CSS",
        "Experience with design tools (Figma, Sketch, Adobe Creative Suite)",
        "Understanding of UX principles and design systems",
        "Knowledge of accessibility standards (WCAG)",
        "Experience with user research and testing methodologies",
        "Portfolio demonstrating UI/UX work",
      ],
    },
    {
      id: "mongodb-dba",
      title: "MongoDB Database Administrator",
      department: "Infrastructure",
      location: "Remote / Hybrid",
      icon: Database,
      description:
        "Manage and optimize our MongoDB infrastructure to ensure high performance and reliability.",
      responsibilities: [
        "Design and maintain MongoDB database architecture",
        "Implement database security and access controls",
        "Monitor and optimize database performance",
        "Plan and execute database migrations and upgrades",
        "Develop backup and disaster recovery procedures",
        "Troubleshoot database issues and provide support",
      ],
      requirements: [
        "Extensive experience with MongoDB administration and optimization",
        "Knowledge of database security and compliance requirements",
        "Experience with cloud-based MongoDB deployments",
        "Understanding of database backup and recovery procedures",
        "Familiarity with monitoring and alerting tools",
        "Strong troubleshooting and problem-solving skills",
      ],
    },
    {
      id: "azure-engineer",
      title: "Azure Engineer",
      department: "Infrastructure",
      location: "Remote / Hybrid",
      icon: Cloud,
      description:
        "Design, implement, and maintain our Azure cloud infrastructure and services.",
      responsibilities: [
        "Design and implement Azure cloud solutions",
        "Manage Azure resources and services (App Service, Functions, Cosmos DB)",
        "Implement infrastructure as code using ARM templates or Bicep",
        "Configure monitoring, logging, and alerting systems",
        "Ensure security and compliance of cloud infrastructure",
        "Optimize costs and performance of Azure resources",
      ],
      requirements: [
        "Strong experience with Microsoft Azure services and administration",
        "Knowledge of infrastructure as code (ARM, Bicep, Terraform)",
        "Experience with Azure DevOps and CI/CD pipelines",
        "Understanding of cloud security and compliance",
        "Familiarity with monitoring and logging tools",
        "Azure certifications preferred",
      ],
    },
    {
      id: "generative-ai-specialist",
      title: "Generative AI Specialist",
      department: "AI Research",
      location: "Remote / Hybrid",
      icon: Brain,
      description:
        "Lead the development of generative AI solutions for document processing and data generation.",
      responsibilities: [
        "Research and implement generative AI models and techniques",
        "Develop custom AI models for document understanding",
        "Optimize generative models for specific use cases",
        "Collaborate with product teams to integrate AI capabilities",
        "Stay current with latest AI research and developments",
        "Ensure ethical and responsible AI development",
      ],
      requirements: [
        "Deep understanding of generative AI models (GPT, DALL-E, etc.)",
        "Experience with large language models and transformers",
        "Strong background in machine learning and deep learning",
        "Proficiency in Python and AI frameworks",
        "Experience with model fine-tuning and optimization",
        "Understanding of AI ethics and responsible development",
      ],
    },
    {
      id: "ml-engineer",
      title: "Machine Learning Engineer",
      department: "AI Research",
      location: "Remote / Hybrid",
      icon: Zap,
      description:
        "Build scalable machine learning systems and pipelines for production environments.",
      responsibilities: [
        "Design and implement ML pipelines and workflows",
        "Develop and deploy machine learning models to production",
        "Optimize model performance and scalability",
        "Implement MLOps practices and tools",
        "Monitor and maintain ML systems in production",
        "Collaborate with data scientists and software engineers",
      ],
      requirements: [
        "Strong experience with ML frameworks (TensorFlow, PyTorch, scikit-learn)",
        "Knowledge of MLOps and model deployment strategies",
        "Experience with cloud platforms and containerization",
        "Understanding of data engineering and ETL processes",
        "Proficiency in Python and related ML libraries",
        "Experience with monitoring and observability tools",
      ],
    },
    {
      id: "edi-specialist",
      title: "EDI Specialist",
      department: "Integration",
      location: "Remote / Hybrid",
      icon: Shield,
      description:
        "Specialize in EDI (Electronic Data Interchange) implementation and maintenance for logistics systems.",
      responsibilities: [
        "Design and implement EDI solutions for trading partners",
        "Develop and maintain EDI mapping and translation logic",
        "Troubleshoot EDI transmission issues and errors",
        "Ensure compliance with EDI standards (X12, EDIFACT)",
        "Collaborate with logistics partners on EDI integration",
        "Document EDI processes and procedures",
      ],
      requirements: [
        "Strong understanding of EDI standards and protocols",
        "Experience with EDI mapping and translation tools",
        "Knowledge of logistics and supply chain processes",
        "Familiarity with X12 transaction sets (204, 211, 214, etc.)",
        "Experience with EDI testing and validation",
        "Strong analytical and problem-solving skills",
      ],
    },
    {
      id: "product-manager",
      title: "Product Manager",
      department: "Product",
      location: "Remote / Hybrid",
      icon: Target,
      description:
        "Lead product strategy and development for our AI-powered logistics platform.",
      responsibilities: [
        "Define product vision, strategy, and roadmap",
        "Gather and prioritize product requirements",
        "Collaborate with engineering, design, and business teams",
        "Analyze market trends and competitive landscape",
        "Define and track product metrics and KPIs",
        "Lead product launches and go-to-market strategies",
      ],
      requirements: [
        "Experience in product management for B2B SaaS products",
        "Strong analytical and strategic thinking skills",
        "Experience with agile development methodologies",
        "Knowledge of logistics or supply chain industry preferred",
        "Excellent communication and stakeholder management skills",
        "Experience with product analytics and user research",
      ],
    },
    {
      id: "sales",
      title: "Sales Representative",
      department: "Sales",
      location: "Remote / Hybrid",
      icon: Briefcase,
      description:
        "Drive revenue growth by selling our AI-powered logistics solutions to transportation and logistics companies.",
      responsibilities: [
        "Prospect and qualify new business opportunities",
        "Conduct product demonstrations and presentations",
        "Develop and maintain relationships with prospects and customers",
        "Negotiate contracts and close deals",
        "Collaborate with marketing and product teams",
        "Meet and exceed sales targets and quotas",
      ],
      requirements: [
        "Proven track record in B2B sales, preferably in logistics or technology",
        "Strong presentation and communication skills",
        "Experience with CRM systems and sales processes",
        "Knowledge of logistics or supply chain industry preferred",
        "Ability to understand technical products and solutions",
        "Self-motivated and results-oriented",
      ],
    },
    {
      id: "transportation-operations-manager",
      title: "Transportation Operations Manager",
      department: "Operations",
      location: "Remote / Hybrid",
      icon: Truck,
      description:
        "Manage transportation operations and ensure smooth delivery of our AI solutions to logistics partners.",
      responsibilities: [
        "Oversee transportation and logistics operations",
        "Develop and implement operational strategies",
        "Manage relationships with transportation partners",
        "Optimize operational efficiency and cost-effectiveness",
        "Ensure compliance with transportation regulations",
        "Lead and develop operational teams",
      ],
      requirements: [
        "Extensive experience in transportation and logistics management",
        "Knowledge of transportation regulations and compliance",
        "Experience with transportation management systems",
        "Strong leadership and team management skills",
        "Understanding of supply chain and logistics processes",
        "Excellent problem-solving and decision-making abilities",
      ],
    },
    {
      id: "azure-marketplace-specialist",
      title: "Azure Marketplace Specialist",
      department: "Business Development",
      location: "Remote / Hybrid",
      icon: ShoppingCart,
      description:
        "Drive growth and adoption of our AI solutions through Microsoft Azure Marketplace.",
      responsibilities: [
        "Manage and optimize our Azure Marketplace presence",
        "Develop go-to-market strategies for Azure Marketplace",
        "Coordinate with Microsoft teams for marketplace optimization",
        "Analyze marketplace performance and customer acquisition metrics",
        "Create compelling marketplace listings and marketing materials",
        "Support customer onboarding and success through marketplace",
        "Monitor marketplace trends and competitive landscape",
      ],
      requirements: [
        "Experience with Microsoft Azure Marketplace or similar cloud marketplaces",
        "Understanding of B2B SaaS sales and marketing strategies",
        "Knowledge of Microsoft partner ecosystem and programs",
        "Experience with marketplace analytics and performance optimization",
        "Strong project management and cross-functional collaboration skills",
        "Familiarity with logistics or supply chain industry preferred",
      ],
    },
    {
      id: "aws-marketplace-specialist",
      title: "AWS Marketplace Specialist",
      department: "Business Development",
      location: "Remote / Hybrid",
      icon: ShoppingCart,
      description:
        "Expand our reach and drive revenue through Amazon Web Services Marketplace.",
      responsibilities: [
        "Manage and optimize our AWS Marketplace presence",
        "Develop strategic partnerships with AWS teams",
        "Create and maintain marketplace listings and documentation",
        "Analyze marketplace performance and customer acquisition data",
        "Coordinate with AWS partner programs and initiatives",
        "Support customer success and onboarding through marketplace",
        "Monitor AWS marketplace trends and competitive positioning",
      ],
      requirements: [
        "Experience with AWS Marketplace or similar cloud marketplaces",
        "Understanding of AWS partner ecosystem and programs",
        "Knowledge of B2B SaaS sales and marketplace strategies",
        "Experience with marketplace analytics and optimization",
        "Strong communication and relationship management skills",
        "Familiarity with logistics or supply chain industry preferred",
      ],
    },
    {
      id: "google-marketplace-specialist",
      title: "Google Marketplace Specialist",
      department: "Business Development",
      location: "Remote / Hybrid",
      icon: ShoppingCart,
      description:
        "Drive adoption and growth of our AI solutions through Google Cloud Marketplace.",
      responsibilities: [
        "Manage and optimize our Google Cloud Marketplace presence",
        "Develop partnerships with Google Cloud teams and programs",
        "Create compelling marketplace listings and marketing content",
        "Analyze marketplace performance and customer acquisition metrics",
        "Coordinate with Google partner ecosystem and initiatives",
        "Support customer onboarding and success through marketplace",
        "Monitor Google Cloud marketplace trends and opportunities",
      ],
      requirements: [
        "Experience with Google Cloud Marketplace or similar cloud marketplaces",
        "Understanding of Google Cloud partner ecosystem and programs",
        "Knowledge of B2B SaaS sales and marketplace strategies",
        "Experience with marketplace analytics and performance optimization",
        "Strong project management and cross-functional collaboration skills",
        "Familiarity with logistics or supply chain industry preferred",
      ],
    },
    {
      id: "digital-marketing-specialist",
      title: "Digital Marketing Specialist",
      department: "Marketing",
      location: "Remote / Hybrid",
      icon: Megaphone,
      description:
        "Drive brand awareness and lead generation through comprehensive digital marketing strategies for our AI-powered logistics platform.",
      responsibilities: [
        "Develop and execute comprehensive digital marketing campaigns",
        "Manage social media presence across multiple platforms",
        "Create and optimize content marketing strategies",
        "Implement SEO and SEM campaigns to drive organic and paid traffic",
        "Analyze marketing performance metrics and optimize campaigns",
        "Collaborate with sales and product teams on go-to-market strategies",
        "Manage email marketing campaigns and lead nurturing programs",
        "Create compelling marketing materials and collateral",
      ],
      requirements: [
        "Proven experience in B2B digital marketing, preferably in technology or logistics",
        "Strong knowledge of digital marketing tools and platforms",
        "Experience with Google Analytics, Google Ads, and social media advertising",
        "Proficiency in content creation and marketing automation tools",
        "Understanding of SEO best practices and content marketing strategies",
        "Strong analytical skills and data-driven approach to marketing",
        "Experience with CRM systems and marketing attribution",
        "Excellent written and verbal communication skills",
      ],
    },
  ];

  return (
    <div className="min-h-screen text-white landing-dark">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <a href="/" className="text-2xl font-bold gradient-text-purple">
                SnapLading
              </a>
            </div>
            <div className="flex items-center gap-4">
              {auth0Authenticated || legacyAuth ? (
                <>
                  <Button
                    size="sm"
                    className="button-purple button-modern"
                    asChild
                  >
                    <a href="/app/upload">Dashboard</a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-300 hover:text-white"
                    onClick={() => {
                      if (auth0Authenticated) {
                        auth0Logout();
                      } else {
                        legacyLogout();
                      }
                    }}
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-300 hover:text-white"
                    onClick={handleSignIn}
                  >
                    Sign In
                  </Button>
                  <Button
                    size="sm"
                    className="button-purple button-modern"
                    onClick={handleSignUp}
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Video Background */}
      <section className="pt-32 pb-20 px-4 lg:px-8 relative min-h-screen flex items-center">
        {/* Background Video */}
        <video
          src="/videos/fullsection.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: -2 }}
        >
          Your browser does not support the video tag.
        </video>

        {/* Video Overlay for better text readability */}
        <div
          className="absolute inset-0"
          style={{
            background: "rgba(0, 0, 0, 0.4)",
            zIndex: -1,
          }}
        ></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/10 shadow-2xl">
            <div className="text-center space-y-8">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight hero-text-animate">
                <span className="text-white">Join Our </span>
                <span className="gradient-text-purple">Team</span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto hero-text-animate">
                Help us revolutionize logistics with AI. We're building the
                future of document processing and data extraction for the
                transportation industry.
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 hero-text-animate">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-500" />
                  <span>Remote-first culture</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-purple-500" />
                  <span>Cutting-edge AI technology</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-500" />
                  <span>Competitive benefits</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-20 px-4 lg:px-8 gradient-bg-purple-grey">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white">
              Open Positions
            </h2>
            <p className="text-xl text-gray-300">
              Find your perfect role in our growing team
            </p>
          </div>

          <div className="grid gap-8">
            {jobPositions.map((job) => (
              <Card
                key={job.id}
                className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-purple-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <job.icon className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl text-white">
                          {job.title}
                        </CardTitle>
                        <CardDescription className="text-base text-gray-400">
                          {job.department} • {job.location}
                        </CardDescription>
                      </div>
                    </div>
                    {/* Removed job.type badge and job.experience display */}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-gray-300">{job.description}</p>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-white mb-3">
                        Responsibilities
                      </h4>
                      <ul className="space-y-2 text-sm text-gray-400">
                        {job.responsibilities.map((resp, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <ArrowRight className="w-3 h-3 text-purple-500 mt-1 flex-shrink-0" />
                            {resp}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-white mb-3">
                        Requirements
                      </h4>
                      <ul className="space-y-2 text-sm text-gray-400">
                        {job.requirements.map((req, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <ArrowRight className="w-3 h-3 text-purple-500 mt-1 flex-shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Benefits section removed */}

                  <Button
                    className="w-full button-purple button-modern"
                    size="lg"
                    asChild
                  >
                    <a
                      href={`mailto:careers@snaplading.com?subject=Apply for ${job.title.toLowerCase()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Apply for {job.title}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company Culture Section */}
      <section className="py-20 px-4 lg:px-8 gradient-bg-purple-grey">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white">
              Why Join SnapLading?
            </h2>
            <p className="text-xl text-gray-300">
              Be part of a team that's transforming logistics with AI
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-purple-400" />
                </div>
                <CardTitle className="text-2xl text-white">
                  Cutting-Edge AI
                </CardTitle>
                <CardDescription className="text-base text-gray-400">
                  Work with the latest AI technologies and help shape the future
                  of document processing.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
                <CardTitle className="text-2xl text-white">
                  Remote-First Culture
                </CardTitle>
                <CardDescription className="text-base text-gray-400">
                  Work from anywhere with flexible schedules and a supportive
                  team environment.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 card-lift bg-gray-900 border-gray-800 card-glow-purple">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-purple-400" />
                </div>
                <CardTitle className="text-2xl text-white">
                  Rapid Growth
                </CardTitle>
                <CardDescription className="text-base text-gray-400">
                  Join a fast-growing company with opportunities for career
                  advancement and skill development.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 lg:px-8 gradient-bg-purple-grey">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <h3 className="text-2xl font-bold mb-4 gradient-text-purple">
                SnapLading
              </h3>
              <p className="text-sm text-white">
                AI agent bill of lading processing for modern logistics.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-white">Product</h4>
              <ul className="space-y-2 text-sm text-white">
                <li>
                  <a
                    href="/docs"
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Documentation
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-white">Company</h4>
              <ul className="space-y-2 text-sm text-white">
                <li>
                  <a
                    href="/careers"
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Careers
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-white">Legal</h4>
              <ul className="space-y-2 text-sm text-black">
                <li>
                  <a
                    href="/privacy"
                    className="text-black hover:text-purple-400 transition-colors"
                  >
                    Privacy
                  </a>
                </li>
                <li>
                  <a
                    href="/terms"
                    className="text-black hover:text-purple-400 transition-colors"
                  >
                    Terms
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-black">
              © {new Date().getFullYear()} Open Verify Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
